import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import CartPricingContext, { useCartPricing, useCartPricingError } from '../../contexts/CartPricingContext';
import CheckoutContext from '../../lib/CheckoutContext';
import { giftableTrackers, hardwareFirstGiftCartItems } from '../../lib/hardwareFirstGiftCart';
import useHardwareFirstGiftingOffered from '../../lib/hardwareFirstGifting';
import {
  giftPlanPerTrackerInCents,
  HardwareFirstGiftTermMonths,
  OfferedGiftPlanTerm,
  offeredGiftPlanTerms,
  sharedGiftRenewalPriceInCents,
} from '../../lib/hardwareFirstGiftPlans';
import * as types from '../../types';

interface HardwareFirstGiftPricing {
  planTerms: OfferedGiftPlanTerm[];
  renewalPriceInCents?: number;
}

export default function useHardwareFirstGiftPricing(): HardwareFirstGiftPricing {
  const offered = useHardwareFirstGiftingOffered();
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  const pricing = useContext(CartPricingContext)?.pricing;
  if (!offered || !pricing) {
    return { planTerms: [] };
  }

  // a term pricing and purchase would not send as a gift is not offered
  const planTerms = offeredGiftPlanTerms(pricing.giftPlanTerms ?? []).filter(
    ({ months }) => !!hardwareFirstGiftCartItems({ ...cart, isGift: true, giftTermMonths: months }, products),
  );
  return {
    planTerms,
    renewalPriceInCents: sharedGiftRenewalPriceInCents(pricing.giftRenewalPrices ?? {}),
  };
}

/** the term the cart bills, only when pricing and purchase send it as a gift */
export function useSelectedGiftPlanTerm(): OfferedGiftPlanTerm | undefined {
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  const { planTerms } = useHardwareFirstGiftPricing();
  const billed = hardwareFirstGiftCartItems(cart, products) && 'giftTermMonths' in cart;
  return billed ? planTerms.find(({ months }) => months === cart.giftTermMonths) : undefined;
}

interface GiftedTrackerPlan {
  trackerIds: Set<types.CartItemID>;
  months: HardwareFirstGiftTermMonths;
  planInCents: number;
  planListInCents: number;
}

/** the gifted trackers and each one's prepaid plan, for their lines; undefined unless web priced the cart as a gift */
export function useGiftedTrackerPlan(): GiftedTrackerPlan | undefined {
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  const { lineItems } = useCartPricing();
  const pricingError = useCartPricingError();
  const term = useSelectedGiftPlanTerm();
  const months = pricingError ? undefined : term?.months;
  const planListInCents = term?.listAmountInCents ?? 0;

  return useMemo(() => {
    if (!months) {
      return undefined;
    }
    const trackers = giftableTrackers(cart, products);
    const planInCents = giftPlanPerTrackerInCents(
      lineItems,
      trackers.reduce((units, tracker) => units + tracker.quantity, 0),
    );
    return planInCents === undefined
      ? undefined
      : { trackerIds: new Set(trackers.map(({ cartItemId }) => cartItemId)), months, planInCents, planListInCents };
  }, [cart, products, lineItems, months, planListInCents]);
}
